import { ORDERED_SIZES_ARRAY } from './constants';
import { DOCUMENT_STATUS } from './enum/DocumentStatus';
import { Cart, CartItem } from './models/order';
import { SizeClothingProduct } from './models/product';

export const camelToSnakeCase = (str: string) => {
    return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
};

export const snakeToCamel = (str: string): string => {
    return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
};

// Convert symbols in strings to strings suitable for translation keys
const symbolMap: { [key: string]: string } = {
    Ø: 'diam',
    µ: 'micro',
    μ: 'micro', // different codepoint
    '°': 'deg',
    '–': '-', // en-dash -> hyphen
    '—': '-', // em-dash -> hyphen
    '&': 'and',
    '%': 'pc',
};
const symbolRegex = new RegExp(
    Object.keys(symbolMap)
        .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|'),
    'g',
);

export const stringToSnakeCase = (str: string): string => {
    return str
        .normalize('NFD')
        .replace(symbolRegex, (match) => symbolMap[match])
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, '_')
        .replace(/[^\w_]+/g, '')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '');
};

export const convertNumberWithoutCurrency = (price: number, digits = 2): string => {
    const formatter = new Intl.NumberFormat('it-IT', {
        style: 'decimal',
        minimumFractionDigits: digits,
    });
    if (price === -1) {
        return formatter.formatToParts().find((part) => part.type === 'currency')?.value || '€';
    }
    if (price > 0) {
        return `${formatter.format(price)}`;
    }
    return '-';
};

export const convertNumberToCurrency = (
    price: number,
    currency = 'EUR',
    digits = 2,
    style: 'currency' | 'decimal' = 'currency',
): string => {
    const formatter = new Intl.NumberFormat('it-IT', {
        style,
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
        currency,
    });
    if (price === -1) {
        return formatter.formatToParts().find((part) => part.type === 'currency')?.value || '€';
    }
    if (price >= 0) {
        return `${formatter.format(price)}`;
    }
    return '-';
};

export const sortArrayBySize = (items: SizeClothingProduct[]): SizeClothingProduct[] => {
    const sizeIndexMap: { [key: string]: number } = ORDERED_SIZES_ARRAY.reduce(
        (map, sizeCode, index) => {
            map[sizeCode] = index;
            return map;
        },
        {} as { [key: string]: number },
    );

    // Funzione di confronto per il metodo sort
    const compareSizes = (a: SizeClothingProduct, b: SizeClothingProduct): number => {
        return sizeIndexMap[a.id] - sizeIndexMap[b.id];
    };

    // Ordinare l'array di oggetti
    items.sort(compareSizes);

    return items;
};

// export const formatPrice = (amount: number, language = 'it', digits = 2): string => {
//     return amount.toLocaleString(language, { minimumFractionDigits: digits, maximumFractionDigits: digits });
// };

// export const getCurrencySign = (currency = 'EUR'): string => {
//     return (
//         new Intl.NumberFormat(window.berardi_accountUserData.language, { style: 'currency', currency })
//             .formatToParts()
//             .find((part) => part.type === 'currency')?.value || '€'
//     );
// };

export const addProductToCart = (cartItem: CartItem, currency: string) => {
    const cart: Cart = localStorage.getItem('br_cartProducts')
        ? JSON.parse(localStorage.getItem('br_cartProducts') as string)
        : { currency: currency, items: [] };
    const foundProductInCart = cart.items.find((product) => {
        return (
            product.sku === cartItem.sku &&
            (!cartItem.colorId || product.colorId === cartItem.colorId) &&
            (!cartItem.sizeId || product.sizeId === cartItem.sizeId)
        );
    });
    let newCartItemsArray: CartItem[] = [];
    if (foundProductInCart) {
        newCartItemsArray = cart.items.map((item) => {
            if (
                item.sku === cartItem.sku &&
                (!cartItem.colorId || item.colorId === cartItem.colorId) &&
                (!cartItem.sizeId || item.sizeId === cartItem.sizeId)
            ) {
                return {
                    ...item,
                    quantity: item.quantity + cartItem.quantity,
                };
            } else {
                return item;
            }
        });
    } else {
        newCartItemsArray = [...cart.items, cartItem];
    }
    localStorage.setItem('br_cartProducts', JSON.stringify({ ...cart, items: newCartItemsArray }));
    window.berardi_accountCart = { ...cart, items: newCartItemsArray, itemNumber: newCartItemsArray.length };
    window.dispatchEvent(new Event('cart-updated'));
};

export const getQuoteStatus = (status: string) => {
    let quoteStatus = DOCUMENT_STATUS.pending;
    switch (status) {
        case 'Created':
            quoteStatus = DOCUMENT_STATUS.created;
            break;
        case 'Confirmed':
            quoteStatus = DOCUMENT_STATUS.confirmed;
            break;
        case 'Cancelled':
            quoteStatus = DOCUMENT_STATUS.cancelled;
            break;
        case 'Approved':
            quoteStatus = DOCUMENT_STATUS.approved;
            break;

        default:
            quoteStatus = DOCUMENT_STATUS.pending;
            break;
    }
    return quoteStatus;
};
